import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Patient, PatientWritePayload } from '@/api/patients/patientsApi.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { FieldError } from '@/components/ui/fieldError';
import { DatePicker } from '@/components/ui/datePicker';
import { Textarea } from '@/components/ui/textarea';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { Icon } from '@/components/common/Icon';
import { resolvePatientOptionLabel } from '@/api/patientOptions/patientOptionsApi';
import { PATIENT_SEX_OPTIONS } from '@/constants/patients';
import {
  useCreatePatientMutation,
  useUpdatePatientMutation,
} from '@/hooks/usePatientMutations';
import { useWorkspacePatientOptionsQuery } from '@/hooks/useWorkspacePatientOptions';
import { cn } from '@/lib/utils';
import { formatDecimalAsYouType } from '@/utils/inputFormatting';
import { isBlank, isDateInFuture, isPositiveNumber } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { OwnerSection } from './OwnerSection';
import {
  createOwnerDraft,
  isOwnerDraftValid,
  toOwnerChoice,
  type OwnerDraft,
  type OwnerMode,
} from './ownerDraft';

const CREATE_OWNER_MODES: OwnerMode[] = ['later', 'existing', 'new'];

interface PatientFormModalProps {
  clientId: string | null;
  patient: Patient | null;
  onClose: () => void;
}

function initial(patient: Patient | null, field: keyof Patient): string {
  if (patient === null) {
    return '';
  }
  const value = patient[field];
  return typeof value === 'string' ? value : '';
}

export function PatientFormModal({ clientId, patient, onClose }: PatientFormModalProps) {
  const { t } = useTranslation(['patients', 'common']);
  const isEditing = patient !== null;
  const needsOwnerChoice = clientId === null && isEditing === false;
  const optionsQuery = useWorkspacePatientOptionsQuery();
  const createMutation = useCreatePatientMutation();
  const updateMutation = useUpdatePatientMutation();
  const [ownerDraft, setOwnerDraft] = useState<OwnerDraft>(createOwnerDraft('later'));

  const speciesOptions = optionsQuery.data === undefined ? [] : optionsQuery.data.species;
  const warningOptions = optionsQuery.data === undefined ? [] : optionsQuery.data.warnings;

  const firstSpecies = speciesOptions[0];
  const defaultSpecies =
    patient !== null && patient.speciesCode !== null
      ? patient.speciesCode
      : firstSpecies === undefined
        ? ''
        : firstSpecies.code;

  const [name, setName] = useState(initial(patient, 'name'));
  const [species, setSpecies] = useState(defaultSpecies);
  const [breed, setBreed] = useState(initial(patient, 'breed'));
  const [sex, setSex] = useState(patient !== null && patient.sex !== null ? patient.sex : '');
  const [dateOfBirth, setDateOfBirth] = useState(
    patient !== null && patient.dateOfBirth !== null ? patient.dateOfBirth : ''
  );
  const [weight, setWeight] = useState(initial(patient, 'weight'));
  const [neutered, setNeutered] = useState(patient !== null && patient.neutered === true);
  const [mainIssue, setMainIssue] = useState(initial(patient, 'mainIssue'));
  const [diagnosis, setDiagnosis] = useState(initial(patient, 'diagnosis'));
  const [medication, setMedication] = useState(initial(patient, 'medication'));
  const [pastSurgeries, setPastSurgeries] = useState(initial(patient, 'pastSurgeries'));
  const [allergies, setAllergies] = useState(initial(patient, 'allergies'));
  const [selectedWarnings, setSelectedWarnings] = useState<string[]>(
    patient === null ? [] : patient.warningCodes
  );
  const [showErrors, setShowErrors] = useState(false);

  const speciesValue = species === '' ? defaultSpecies : species;
  const isPending = createMutation.isPending || updateMutation.isPending;

  const nameError = showErrors === true && isBlank(name) ? 'validation:requiredField' : null;
  const weightError =
    showErrors === true && weight.trim().length > 0 && isPositiveNumber(weight) === false
      ? 'validation:positiveNumber'
      : null;
  const dateOfBirthError =
    showErrors === true && isDateInFuture(dateOfBirth) ? 'validation:dateNotFuture' : null;

  function toNullable(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }

  function toggleWarning(label: string) {
    setSelectedWarnings(function update(previous) {
      return previous.includes(label)
        ? previous.filter(function keep(item) {
            return item !== label;
          })
        : [...previous, label];
    });
  }

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    const nameInvalid = isBlank(name);
    const ownerInvalid = needsOwnerChoice === true && isOwnerDraftValid(ownerDraft) === false;
    const weightInvalid = weight.trim().length > 0 && isPositiveNumber(weight) === false;
    const dateOfBirthInvalid = isDateInFuture(dateOfBirth);
    if (nameInvalid || ownerInvalid || weightInvalid || dateOfBirthInvalid) {
      setShowErrors(true);
      return;
    }
    const payload: PatientWritePayload = {
      name: name.trim(),
      speciesCode: toNullable(speciesValue),
      breed: toNullable(breed),
      sex: sex === '' ? null : sex,
      dateOfBirth: dateOfBirth === '' ? null : dateOfBirth,
      weight: toNullable(weight),
      neutered,
      mainIssue: toNullable(mainIssue),
      diagnosis: toNullable(diagnosis),
      medication: toNullable(medication),
      pastSurgeries: toNullable(pastSurgeries),
      allergies: toNullable(allergies),
      warningCodes: selectedWarnings,
    };

    function handleError(saveError: unknown) {
      const backendMessage = resolveApiErrorMessage(saveError);
      showErrorNotification(backendMessage.length > 0 ? backendMessage : t('patients:form.saveError'));
    }
    function handleSuccess() {
      showSuccessNotification(t('patients:form.saved'));
      onClose();
    }

    if (isEditing === true && patient !== null) {
      updateMutation.mutate({ patientId: patient.id, payload }, { onSuccess: handleSuccess, onError: handleError });
      return;
    }
    createMutation.mutate(
      {
        owner: clientId === null ? toOwnerChoice(ownerDraft) : { kind: 'existing', clientId },
        payload,
      },
      { onSuccess: handleSuccess, onError: handleError }
    );
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <form
        onSubmit={submit}
        className="w-full max-w-4xl max-h-[92vh] flex flex-col bg-zoowhite border border-zooborder rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zooborder">
          <h2 className="font-bold text-zooindigo">
            {isEditing === true ? t('patients:form.editTitle') : t('patients:form.addTitle')}
          </h2>
          <button type="button" aria-label={t('common:actions.close')} onClick={onClose}>
            <Icon name="X" className="text-zoomuted" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {/* Left column — identity */}
            <div className="space-y-4">
              {needsOwnerChoice === true ? (
                <div className="sm:rounded-2xl sm:border sm:border-zooborder sm:p-4">
                  <OwnerSection
                    draft={ownerDraft}
                    modes={CREATE_OWNER_MODES}
                    showErrors={showErrors}
                    onChange={setOwnerDraft}
                  />
                </div>
              ) : null}
              <Field id="patient-name" label={t('patients:form.name')}>
                <Input
                  id="patient-name"
                  value={name}
                  hasError={nameError !== null}
                  placeholder={t('patients:form.namePlaceholder')}
                  onChange={function update(changeEvent) {
                    setName(changeEvent.target.value);
                  }}
                />
                <FieldError messageKey={nameError} />
              </Field>
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                <Field id="patient-species" label={t('patients:form.species')}>
                  <SearchableSelect
                    inputId="patient-species"
                    value={speciesValue}
                    options={speciesOptions.map(function toOption(option) {
                      return { value: option.code, label: resolvePatientOptionLabel(option) };
                    })}
                    onChange={setSpecies}
                  />
                </Field>
                <Field id="patient-breed" label={t('patients:form.breed')}>
                  <Input
                    id="patient-breed"
                    value={breed}
                    placeholder={t('patients:form.breedPlaceholder')}
                    onChange={function update(changeEvent) {
                      setBreed(changeEvent.target.value);
                    }}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                <Field id="patient-sex" label={t('patients:form.sex')}>
                  <SearchableSelect
                    inputId="patient-sex"
                    value={sex}
                    isClearable
                    placeholder={t('patients:form.selectSex')}
                    options={PATIENT_SEX_OPTIONS.map(function toOption(option) {
                      return { value: option, label: t('common:sex.' + option) };
                    })}
                    onChange={setSex}
                  />
                </Field>
                <Field id="patient-dob" label={t('patients:form.dateOfBirth')}>
                  <DatePicker
                    id="patient-dob"
                    value={dateOfBirth}
                    hasError={dateOfBirthError !== null}
                    placeholder={t('patients:form.dateOfBirthPlaceholder')}
                    onChange={setDateOfBirth}
                  />
                  <FieldError messageKey={dateOfBirthError} />
                </Field>
              </div>
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3 items-end">
                <Field id="patient-weight" label={t('patients:form.weight')}>
                  <div className="relative">
                    <Input
                      id="patient-weight"
                      inputMode="decimal"
                      value={weight}
                      hasError={weightError !== null}
                      placeholder={t('patients:form.weightPlaceholder')}
                      className="pr-11"
                      onChange={function update(changeEvent) {
                        setWeight(formatDecimalAsYouType(changeEvent.target.value));
                      }}
                    />
                    <span className="absolute inset-y-0 right-3 flex items-center text-sm text-zoomuted pointer-events-none">
                      {t('patients:form.weightUnit')}
                    </span>
                  </div>
                  <FieldError messageKey={weightError} />
                </Field>
                <label className="flex items-center gap-2 text-sm text-zooindigo py-2.5">
                  <input
                    type="checkbox"
                    checked={neutered}
                    onChange={function update(changeEvent) {
                      setNeutered(changeEvent.target.checked);
                    }}
                    className="w-4 h-4 accent-zooprimary"
                  />
                  {t('patients:form.neutered')}
                </label>
              </div>
              <div className="space-y-1.5">
                <Label>{t('patients:form.warnings')}</Label>
                <div className="flex flex-wrap gap-2">
                  {warningOptions.map(function renderWarning(option) {
                    const isSelected = selectedWarnings.includes(option.code);
                    return (
                      <button
                        key={option.code}
                        type="button"
                        onClick={function toggle() {
                          toggleWarning(option.code);
                        }}
                        className={cn(
                          'px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors',
                          isSelected === true
                            ? 'bg-zoowarningbg text-zoowarningtext border-transparent'
                            : 'bg-zoowhite text-zoomuted border-zooborder'
                        )}
                      >
                        {resolvePatientOptionLabel(option)}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-zoomuted">{t('patients:form.warningsHint')}</p>
              </div>
            </div>

            {/* Right column — clinical */}
            <div className="space-y-4">
              <div className="text-xs font-bold uppercase tracking-wider text-zoomuted">
                {t('patients:form.clinicalSection')}
              </div>
              <ClinicalField id="patient-issue" label={t('patients:form.mainIssue')} placeholder={t('patients:form.mainIssuePlaceholder')} value={mainIssue} onChange={setMainIssue} />
              <ClinicalField id="patient-diagnosis" label={t('patients:form.diagnosis')} placeholder={t('patients:form.clinicalPlaceholder')} value={diagnosis} onChange={setDiagnosis} />
              <ClinicalField id="patient-medication" label={t('patients:form.medication')} placeholder={t('patients:form.clinicalPlaceholder')} value={medication} onChange={setMedication} />
              <ClinicalField id="patient-surgeries" label={t('patients:form.pastSurgeries')} placeholder={t('patients:form.clinicalPlaceholder')} value={pastSurgeries} onChange={setPastSurgeries} />
              <ClinicalField id="patient-allergies" label={t('patients:form.allergies')} placeholder={t('patients:form.clinicalPlaceholder')} value={allergies} onChange={setAllergies} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zooborder bg-zoooffwhite">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" isLoading={isPending}>
            {t('patients:form.save')}
          </Button>
        </div>
      </form>
    </ModalOverlay>
  );
}

interface FieldProps {
  id: string;
  label: string;
  children: React.ReactNode;
}

function Field({ id, label, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

interface ClinicalFieldProps {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

function ClinicalField({ id, label, placeholder, value, onChange }: ClinicalFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        rows={2}
        value={value}
        placeholder={placeholder}
        onChange={function update(changeEvent) {
          onChange(changeEvent.target.value);
        }}
      />
    </div>
  );
}
